// Build a self-contained CSS string of @font-face declarations whose
// url() references are inlined as base64 data URIs.
//
// This is used by html-to-image when rendering the vessel flyer to a
// PDF. The SVG foreignObject that html-to-image produces is decoded
// as an *image* by the browser — at that point the CSS rules from the
// surrounding page are NOT applied, and external font URLs can't be
// fetched cross-origin. Without explicit font embedding, the SVG falls
// back to the next family in `font-family` (Manrope → sans-serif),
// which has noticeably wider glyphs than Bebas Neue. Long flyer
// titles like "24M INSHORE TRAWLER" then wrap to two lines and the
// "WORK BOAT" sub-category visually overlaps the second line.
//
// We fetch the Google Fonts stylesheet (which is CORS-friendly and
// returns woff2 URLs for the requesting browser's User-Agent), then
// fetch each woff2 file and rewrite the CSS so every url() is a
// data: URI. The resulting CSS can be passed to html-to-image's
// `fontEmbedCSS` option — html-to-image will skip its own
// document.styleSheets walk (which throws SecurityError on Google
// Fonts) and inline the provided CSS as-is.
//
// The fetch + base64 conversion is heavy enough (~300 KB of inline
// CSS) that we cache the result for the page's lifetime. Subsequent
// PDF generations reuse the cached string.

let cachePromise = null;

// Match Google Fonts woff2 URLs in the stylesheet. The hostname is
// always `fonts.gstatic.com`; the path varies by family and weight.
const GOOGLE_FONT_URL_RE = /url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/g;

async function fetchAsDataUri(url) {
  const buf = await fetch(url).then((r) => {
    if (!r.ok) throw new Error(`Font fetch ${r.status}: ${url}`);
    return r.arrayBuffer();
  });
  const bytes = new Uint8Array(buf);
  // Build base64 in chunks so we don't blow the call stack on large
  // typefaces (Manrope at 8 weights is ~250 KB).
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length))
    );
  }
  return `data:font/woff2;base64,${btoa(binary)}`;
}

async function buildEmbeddedFontCss() {
  // Mirrors the <link> in /app/frontend/public/index.html — adjusting
  // either side requires adjusting both.
  const familiesQuery =
    "family=Bebas+Neue" +
    "&family=Manrope:wght@300;400;500;600;700;800" +
    "&family=IBM+Plex+Mono:wght@400;500;600";
  const cssUrl = `https://fonts.googleapis.com/css2?${familiesQuery}&display=swap`;
  const css = await fetch(cssUrl).then((r) => {
    if (!r.ok) throw new Error(`Google Fonts CSS ${r.status}`);
    return r.text();
  });
  const urls = Array.from(
    new Set(Array.from(css.matchAll(GOOGLE_FONT_URL_RE), (m) => m[1]))
  );
  const replacements = await Promise.all(
    urls.map(async (url) => ({ url, dataUri: await fetchAsDataUri(url) }))
  );
  let result = css;
  for (const { url, dataUri } of replacements) {
    result = result.split(url).join(dataUri);
  }
  return result;
}

export async function getEmbeddedFontCss() {
  // Single-flight: concurrent callers share the same fetch.
  if (!cachePromise) {
    cachePromise = buildEmbeddedFontCss().catch((e) => {
      // Don't poison the cache — let the next caller retry.
      cachePromise = null;
      throw e;
    });
  }
  return cachePromise;
}
